import { get, map, filter, split, chunk, flatMap } from 'lodash';
import { EMenuItemType, IChildMenuItem, IMenuItem, IMenuItemResponse, IChildMenuItemResponse, TAnyItem } from './types';
import { MENU_COLOR_RED, MENU_COLOR_DARK_RED, MENU_COLOR_BLUE, MENU_COLOR_DARK_BLUE } from './constants';

export const getMenuItemColors = (
  menuItem: Partial<IChildMenuItemResponse>,
): Pick<IMenuItem, 'highlightColor1' | 'highlightColor2'> => {
  const menuItemType: EMenuItemType = get(menuItem, 'menuItemType', EMenuItemType.Queue);
  const hasAlert: boolean = get(menuItem, 'hasAlert', false);

  if (menuItemType === EMenuItemType.Queue && hasAlert) {
    return {
      highlightColor1: MENU_COLOR_RED,
      highlightColor2: MENU_COLOR_DARK_RED,
    };
  }

  switch (menuItemType) {
    case EMenuItemType.Queue:
    case EMenuItemType.Link:
      return {
        highlightColor1: MENU_COLOR_BLUE,
        highlightColor2: MENU_COLOR_DARK_BLUE,
      };
    case EMenuItemType.Header:
      return {
        highlightColor1: MENU_COLOR_DARK_BLUE,
        highlightColor2: MENU_COLOR_DARK_BLUE,
      };
    default:
      return {
        highlightColor1: MENU_COLOR_BLUE,
        highlightColor2: MENU_COLOR_BLUE,
      };
  }
};

export const getNewItem = (order: number, parentId: string | null = null): Omit<IChildMenuItem, 'parentId'> => ({
  id: `temp_${parentId ?? ''}_${order}`,
  text: '***New Menu Item***',
  menuItemType: EMenuItemType.Queue,
  link: null,
  hasAlert: false,
  alertCount: 0,
  queueId: null,
  roleIds: [],
  iconClass: null,
  isDeleted: false,
  ...getMenuItemColors({}),
  order,
});

export const getExpandedIds = (menuItems: IMenuItemResponse[]): string[] =>
  map(filter<IMenuItemResponse>(menuItems, ['menuItemType', EMenuItemType.Expandable]), 'id');

export const getDragDropPosData = (pos: string): number[] => {
  const [, parentIndex, childIndex] = map<string, number>(split(pos, '-'), (value) => Number(value));

  return [parentIndex, childIndex];
};

export function insertItem<TItem>(items: TItem[], newItem: TItem, to: number): TItem[] {
  return to === 0
    ? [newItem, ...items]
    : flatMap(chunk<TItem>(items, to), (itemsArr, index) => (index === 0 ? [...itemsArr, newItem] : itemsArr));
}

export const insertChildItem = (menuItems: IMenuItem[], dragItem: TAnyItem, parentIndex: number, childIndex: number) =>
  map<IMenuItem, IMenuItem>(menuItems, (menuItem, index) => {
    if (index !== parentIndex) {
      return menuItem;
    }

    const { id: parentId, childMenuItems } = menuItem;
    let newChildItem: IChildMenuItem;

    if ('childMenuItems' in dragItem) {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { childMenuItems, ...item } = dragItem;
      newChildItem = { ...item, parentId };
    } else {
      newChildItem = { ...dragItem, parentId };
    }

    return {
      ...menuItem,
      menuItemType: EMenuItemType.Expandable,
      childMenuItems: insertItem<IChildMenuItem>(childMenuItems, newChildItem, childIndex),
    };
  });
